/**
 * FilterChip Component
 *
 * A removable chip for displaying active filter values.
 * Used in filter bars to show currently applied filters.
 */

import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, radii, transitions } from '../../tokens';

export interface FilterChipProps {
    /** Chip label */
    label: string;
    /** Handler for removing the chip */
    onRemove?: () => void;
    /** Whether the chip is removable */
    removable?: boolean;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const FilterChip: React.FC<FilterChipProps> = ({ label, onRemove, removable = true, sx }) => (
    <Box
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: colors.primary[500],
            color: colors.neutral[0],
            borderRadius: `${radii.md}px`,
            pl: 1.5,
            pr: removable ? 0.5 : 1.5,
            py: 0.5,
            height: 32,
            transition: transitions.fast,
            ...sx,
        }}>
        <Typography
            sx={{
                fontSize: typographyTokens.fontSize.sm,
                fontWeight: typographyTokens.fontWeight.medium,
                whiteSpace: 'nowrap',
                color: 'white',
            }}>
            {label}
        </Typography>
        {removable && onRemove && (
            <IconButton
                onClick={onRemove}
                size='small'
                sx={{
                    p: 0.25,
                    color: colors.neutral[0],
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                }}>
                <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
        )}
    </Box>
);

export default FilterChip;
